import React, { useState, Children } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon, LayoutDashboardIcon, FolderIcon, FileTextIcon, UsersIcon, SettingsIcon, ReceiptIcon, ListIcon, BuildingIcon } from 'lucide-react';
interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
}
const menuItems: MenuItem[] = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: <LayoutDashboardIcon className="w-5 h-5" data-id="element-145" />,
  path: '/dashboard'
}, {
  id: 'accounts',
  label: 'Accounts',
  icon: <FolderIcon className="w-5 h-5" data-id="element-146" />,
  children: [{
    id: 'coa',
    label: 'COA',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-147" />,
    path: '/coa'
  }, {
    id: 'bank-accounts',
    label: 'Bank Accounts',
    icon: <BuildingIcon className="w-4 h-4" data-id="element-148" />,
    path: '/bank-accounts'
  }]
}, {
  id: 'voucher-bill',
  label: 'Voucher & Bill Management',
  icon: <ReceiptIcon className="w-5 h-5" data-id="element-149" />,
  children: [{
    id: 'receive-voucher-list',
    label: 'Receive Voucher List',
    icon: <ListIcon className="w-4 h-4" data-id="element-150" />,
    path: '/voucher/receive'
  }, {
    id: 'payment-voucher-list',
    label: 'Payment Voucher List',
    icon: <ListIcon className="w-4 h-4" data-id="element-151" />,
    path: '/voucher/payment'
  }, {
    id: 'journal-list',
    label: 'Journal Voucher List',
    icon: <ListIcon className="w-4 h-4" data-id="element-152" />,
    path: '/voucher/journal'
  }, {
    id: 'bank-transaction-list',
    label: 'Bank Transaction List',
    icon: <ListIcon className="w-4 h-4" data-id="element-153" />,
    path: '/bank-transaction'
  }, {
    id: 'bill-payment-list',
    label: 'Bill Payment List',
    icon: <ListIcon className="w-4 h-4" data-id="element-154" />,
    path: '/bill-payment'
  }, {
    id: 'all-vouchers-list',
    label: 'All Vouchers List',
    icon: <ListIcon className="w-4 h-4" data-id="element-155" />,
    path: '/voucher/all'
  }]
}, {
  id: 'parties',
  label: 'Party Management',
  icon: <UsersIcon className="w-5 h-5" data-id="element-156" />,
  path: '/parties'
}, {
  id: 'settings',
  label: 'Settings',
  icon: <SettingsIcon className="w-5 h-5" data-id="element-157" />,
  path: '/settings'
}];
export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['accounts', 'voucher-bill']);
  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]);
  };
  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  const handleNavigation = (item: MenuItem) => {
    if (item.children) {
      toggleMenu(item.id);
    } else if (item.path) {
      navigate(item.path);
    }
  };
  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    const active = isActive(item.path);
    return <div key={item.id} data-id="element-158">
        <button onClick={() => handleNavigation(item)} className={`
            w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded-lg
            transition-colors duration-150
            ${level === 0 ? 'mb-1' : 'mb-0.5 ml-4'}
            ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}
          `} data-id="element-159">
          <div className="flex items-center gap-3" data-id="element-160">
            <span className={active ? 'text-blue-700' : 'text-gray-500'} data-id="element-161">
              {item.icon}
            </span>
            <span data-id="element-162">{item.label}</span>
          </div>
          {hasChildren && <span className="text-gray-400" data-id="element-163">
              {isExpanded ? <ChevronDownIcon className="w-4 h-4" data-id="element-164" /> : <ChevronRightIcon className="w-4 h-4" data-id="element-165" />}
            </span>}
        </button>

        {hasChildren && isExpanded && <div className="mt-1 space-y-0.5" data-id="element-166">
            {item.children!.map(child => renderMenuItem(child, level + 1))}
          </div>}
      </div>;
  };
  return <aside className="w-64 h-screen bg-white border-r border-gray-200 flex flex-col" data-id="element-167">
      {/* Logo/Header */}
      <div className="h-16 flex items-center px-6 border-b border-gray-200" data-id="element-168">
        <h1 className="text-xl font-bold text-gray-900" data-id="element-169">Financial System</h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4" data-id="element-170">
        <div className="space-y-1" data-id="element-171">
          {menuItems.map(item => renderMenuItem(item))}
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200" data-id="element-172">
        <div className="flex items-center gap-3 px-3 py-2" data-id="element-173">
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-174">
            <span className="text-sm font-medium text-gray-600" data-id="element-175">U</span>
          </div>
          <div className="flex-1 min-w-0" data-id="element-176">
            <p className="text-sm font-medium text-gray-900 truncate" data-id="element-177">
              User Name
            </p>
            <p className="text-xs text-gray-500 truncate" data-id="element-178">user@example.com</p>
          </div>
        </div>
      </div>
    </aside>;
}